// Z41: AI-parsed port/carrier names are free text -- all caps, sometimes with a trailing
// ", COUNTRY", sometimes a vessel name standing in for the carrier -- so a plain
// case-sensitive/exact lookup against the ports/carriers tables misses rows that are
// genuinely on file (e.g. parsed "FELIXSTOWE" vs. stored "Felixstowe", or parsed
// "NANSHA, CHINA" vs. stored "Nansha"). This module is the shared cascade used for both.

export interface MatchableCode {
  id?: string
  code: string | null
  name: string
  type?: string | null
  un_locode?: string | null
}

export interface MatchOptions {
  // Restrict candidates to this `type` before comparing names (SEAPORT vs AIRPORT) --
  // carriers have no type, so this is only passed for port lookups.
  type?: string
  // When more than one row's name matches, prefer whichever has this identifier filled in --
  // a stub row created before its code/LOCODE was known shouldn't outrank the canonical one.
  identifierKey?: 'code' | 'un_locode'
}

// UPPER + TRIM + drop a trailing "(CODE)" and/or ", COUNTRY" qualifier -- ports/carriers table
// names are stored proper-case and suffix-free.
//
// Z58.11 (KI26050/KI24133 Bug 2): the ", COUNTRY" strip alone silently failed whenever the AI
// parser's name-with-embedded-code convention (create-autojobs' own comment: "AI parser embeds
// a code in parens on the name itself, e.g. NINGBO (CNNGB)") left a trailing "(ICN)" on the
// string -- `/,\s*[A-Z\s]+$/` requires ONLY uppercase letters/whitespace up to the end, and a
// literal "(" ")" breaks that, so "INCHEON, KOREA (ICN)" normalized to itself, unchanged. That
// left `firstWord` returning "INCHEON," (comma attached) and neither real Incheon airport row
// ("INCHEON AIRPORT", "Heliport Incheon") matched at all in candidatesByName below -- a
// coincidental 3-letter garbage row literally named "INC" (bad seed data, country=PY) won
// instead purely because "INCHEON...".includes("INC") is trivially true. Confirmed live via a
// new inspect_containers debug query against job_air_details/ports.
export function normalizeMatchName(name?: string): string {
  if (!name) return ''
  return name.trim().toUpperCase()
    .replace(/\s*\([A-Z0-9]{2,6}\)\s*$/, '')
    .replace(/,\s*[A-Z\s]+$/, '')
    .trim()
}

function firstWord(name: string): string {
  return name.split(/\s+/)[0] || ''
}

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m
  const prev = Array.from({ length: n + 1 }, (_, j) => j)
  const curr = new Array(n + 1).fill(0)
  for (let i = 1; i <= m; i++) {
    curr[0] = i
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1])
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j]
  }
  return prev[n]
}

function similarity(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length)
  if (maxLen === 0) return 1
  return 1 - levenshtein(a, b) / maxLen
}

const FUZZY_THRESHOLD = 0.75
const FUZZY_MIN_LENGTH = 4
const FIRST_WORD_MIN_LENGTH = 3

// Multiple rows can share a name -- a stub created before the canonical row had its
// code/LOCODE filled in. Narrow to whichever row(s) actually carry the identifier so a
// leftover stub duplicate doesn't beat (or tie with) the real one.
function preferCanonical<T extends MatchableCode>(matches: T[], identifierKey: 'code' | 'un_locode'): T[] {
  if (matches.length <= 1) return matches
  const withIdentifier = matches.filter(m => !!m[identifierKey])
  return withIdentifier.length > 0 ? withIdentifier : matches
}

// Cascade of increasingly loose name comparisons, each stage tried only if the stricter one
// before it found nothing -- so an exact match always wins over a fuzzy one instead of the
// two pools mixing together.
function candidatesByName<T extends MatchableCode>(options: T[], normalized: string): T[] {
  if (!normalized) return []

  const exact = options.filter(o => normalizeMatchName(o.name) === normalized)
  if (exact.length > 0) return exact

  const word = firstWord(normalized)
  const partial = options.filter(o => {
    const oName = normalizeMatchName(o.name)
    if (oName.includes(normalized)) return true
    // Z58.11: `normalized.includes(oName)` alone let a short, coincidentally-prefix-matching
    // name win outright -- a 3-letter garbage row named "INC" (bad seed data) satisfied
    // "INCHEON".includes("INC") even after the normalizeMatchName fix above, with nothing to
    // out-compete it once the real "INCHEON AIRPORT"/"Heliport Incheon" rows also start
    // matching via the word-prefix check below (all three now land in `partial` together,
    // which is ambiguous rather than silently wrong -- `uniqueMatch` requires exactly one
    // candidate to auto-fill, so this makes it correctly fall through to a manual pick instead
    // of confidently resolving to Paraguay). Require oName to carry real signal (not shorter
    // than FUZZY_MIN_LENGTH) before trusting it as a substring of the searched name.
    if (normalized.includes(oName) && oName.length >= FUZZY_MIN_LENGTH) return true
    // Catches a parsed vessel/name string that merely starts with the carrier's name
    // (e.g. "MSC IRINA" against carrier row "MSC (Mediterranean Shipping)").
    return word.length >= FIRST_WORD_MIN_LENGTH && oName.includes(word)
  })
  if (partial.length > 0) return partial

  if (normalized.length < FUZZY_MIN_LENGTH) return []
  return options
    .map(o => ({ o, score: similarity(normalizeMatchName(o.name), normalized) }))
    .filter(({ score }) => score >= FUZZY_THRESHOLD)
    .sort((a, b) => b.score - a.score)
    .map(({ o }) => o)
}

// An exact identifier-code match wins outright over any name comparison -- a parsed code
// like "GBFXT" or "COSU" is unambiguous, whereas a name always needs the cascade above.
export function findAllMatches<T extends MatchableCode>(options: T[], name?: string, opts: MatchOptions = {}): T[] {
  const trimmed = name?.trim().toLowerCase()
  if (!trimmed) return []

  // A row with no `type` at all (legacy data from before that column was backfilled) is kept
  // in every type's candidate pool rather than excluded outright -- it's ambiguous, not wrong.
  const scoped = opts.type ? options.filter(o => o.type === opts.type || o.type == null) : options

  const codeMatches = scoped.filter(o => o.code?.toLowerCase() === trimmed)
  if (codeMatches.length > 0) return codeMatches

  const matches = candidatesByName(scoped, normalizeMatchName(name))
  return opts.identifierKey ? preferCanonical(matches, opts.identifierKey) : matches
}

export function findMatch<T extends MatchableCode>(options: T[], name?: string, opts?: MatchOptions): T | undefined {
  return findAllMatches(options, name, opts)[0]
}

// Only auto-select when the name resolves to exactly one row -- picking the first of several
// remaining candidates silently could auto-fill the wrong port/carrier.
export function uniqueMatch<T extends MatchableCode>(options: T[], name?: string, opts?: MatchOptions): T | undefined {
  const matches = findAllMatches(options, name, opts)
  return matches.length === 1 ? matches[0] : undefined
}

export function findCode<T extends MatchableCode>(options: T[], name?: string, opts?: MatchOptions): string {
  return findMatch(options, name, opts)?.code || ''
}
